import * as React from 'react';
import { useCookies } from 'react-cookie';
import awsRum from '../../awsRum';
import config from '../../config';
import { notifyOrganisationId } from '../../connectWebsocket/persistedOrganisationId';
import { trackDatadogLogin, trackDatadogLogout } from '../../datadogUserTracking';
import { GetLiveUserInfoQuery, useGetLiveUserInfoQuery } from '../../generated/core-api/graphql-react-query';
import isCancelledRequestError from '../../utils/isCancelledRequestError';

interface UseUserInfoOptions {
  enabled?: boolean;
}

export function useUserInfo(token?: string, options: UseUserInfoOptions = {}) {
  const { enabled = true } = options;
  const userInfo = useGetLiveUserInfoQuery<GetLiveUserInfoQuery, Error>({}, {
    enabled: !!token && enabled,
    retry(failureCount, error) {
      if (error?.message === 'User not found') {
        return false;
      }

      return isCancelledRequestError(error) && failureCount < 3;
    },
  });

  React.useEffect(() => {
    if (userInfo.error) {
      console.error('Error getting user info', userInfo.error);
    }
  }, [userInfo.error]);

  React.useEffect(() => {
    if (userInfo.data) {
      const {
        id: userId, email, organisationId, role, isStaff,
      } = userInfo.data.getUserInfo;
      notifyOrganisationId(organisationId);
      awsRum?.addSessionAttributes({
        userId, email, organisationId, role, isStaff,
      });
      trackDatadogLogin({
        userId, email, organisationId, role, isStaff,
      });
    }
  }, [userInfo.data]);
  return userInfo;
}

export function useUserInfoData(): GetLiveUserInfoQuery['getUserInfo'] {
  const userInfo = useUserInfo();
  if (!userInfo.data) {
    throw new Error('User info not loaded');
  }
  return userInfo.data.getUserInfo;
}

function doNothing() { }

interface CookieContextProps {
  removeToken: (logoutMethod?: string) => void;
  setOrgOverride: (value: string) => void;
  setToken: (value: string) => void;
  org: string | undefined;
  token: string | undefined;
}
const CookieContext = React.createContext<CookieContextProps>({
  removeToken: doNothing,
  setOrgOverride: doNothing,
  setToken: doNothing,
  org: undefined,
  token: undefined,
});

export function CookieProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const [cookies, setCookies, removeCookie] = useCookies([
    config.Auth.Cookies.TokenCookieName ?? '',
  ]);
  const token: string | undefined = cookies[config.Auth.Cookies.TokenCookieName ?? ''];

  function setToken(value: string) {
    setCookies(config.Auth.Cookies.TokenCookieName ?? '', value, {
      path: '/',
      sameSite: 'lax',
      domain: config.Auth.Cookies.CookieDomain,
      secure: true,
      maxAge: 3600 * 24 * 365,
    });
  }

  function setOrgOverride(value: string) {
    setCookies(config.Auth.Cookies.OrgOverrideCookieName ?? '', value, {
      path: '/',
      sameSite: 'lax',
      domain: config.Auth.Cookies.CookieDomain,
      secure: true,
      maxAge: 3600 * 24 * 365,
    });
  }

  function removeToken(logoutMethod = 'token_removed') {
    trackDatadogLogout(logoutMethod);
    removeCookie(config.Auth.Cookies.TokenCookieName ?? '', { domain: config.Auth.Cookies.CookieDomain });
    removeCookie('token');
  }

  const org: string | undefined = cookies[config.Auth.Cookies.OrgOverrideCookieName ?? ''];
  const role: string | undefined = cookies[config.Auth.Cookies.RoleOverrideCookieName ?? ''];

  const value = React.useMemo(() => ({
    setToken,
    setOrgOverride,
    removeToken,
    token,
    org,
    role: role ? parseInt(role, 10) : undefined,
  }), [setToken, setOrgOverride, removeToken, token, org, role]);

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
}

export function useCookieContext() { return React.useContext(CookieContext); }
